import type { StringStorage } from "./repository";
import {
  NEW_CARD_DRAFT_SCHEMA_VERSION,
  isNewCardDraft,
  normalizeNewCardDraft,
  type NewCardDraft,
} from "./new-card";

export const NEW_CARD_DRAFT_STORAGE_KEY =
  "convoful.owner-new-card-draft.v1";
export const NEW_CARD_DRAFT_MAX_AGE_MS = 24 * 60 * 60 * 1_000;

interface StoredNewCardDraft {
  kind: "owner_new_card_draft";
  schemaVersion: typeof NEW_CARD_DRAFT_SCHEMA_VERSION;
  ownerUserID: string;
  expiresAt: string;
  draft: NewCardDraft;
}

export interface NewCardDraftLoadResult {
  draft: NewCardDraft | null;
  recoveredInvalidState: boolean;
  expired: boolean;
  ownerMismatch: boolean;
}

export class NewCardDraftStore {
  constructor(
    private readonly storage: StringStorage,
    private readonly now: () => Date = () => new Date(),
  ) {}

  load(ownerUserID: string): NewCardDraftLoadResult {
    const expectedOwner = ownerUserID.trim();
    if (!expectedOwner) {
      throw new Error("A private New Card draft needs an active owner.");
    }
    const raw = this.storage.getItem(NEW_CARD_DRAFT_STORAGE_KEY);
    if (raw === null) return emptyLoadResult();
    let value: unknown;
    try {
      value = JSON.parse(raw);
    } catch {
      this.clear();
      return {
        ...emptyLoadResult(),
        recoveredInvalidState: true,
      };
    }
    if (!isStoredDraft(value)) {
      this.clear();
      return {
        ...emptyLoadResult(),
        recoveredInvalidState: true,
      };
    }
    if (value.ownerUserID !== expectedOwner) {
      this.clear();
      return {
        ...emptyLoadResult(),
        ownerMismatch: true,
      };
    }
    if (new Date(value.expiresAt).valueOf() <= this.now().valueOf()) {
      this.clear();
      return {
        ...emptyLoadResult(),
        expired: true,
      };
    }
    return {
      ...emptyLoadResult(),
      draft: normalizeNewCardDraft(value.draft),
    };
  }

  save(ownerUserID: string, draft: NewCardDraft): void {
    const owner = ownerUserID.trim();
    if (!owner) {
      throw new Error("A private New Card draft needs an active owner.");
    }
    if (!isNewCardDraft(draft)) {
      throw new Error("The private New Card draft is invalid.");
    }
    const createdAt = new Date(draft.createdAt);
    const updatedAt = new Date(draft.updatedAt);
    if (updatedAt.valueOf() < createdAt.valueOf()) {
      throw new Error("The private New Card draft timeline is invalid.");
    }
    const expiresAt = new Date(
      createdAt.valueOf() + NEW_CARD_DRAFT_MAX_AGE_MS,
    );
    if (expiresAt.valueOf() <= this.now().valueOf()) {
      throw new Error("This private New Card draft has expired.");
    }
    const stored: StoredNewCardDraft = {
      kind: "owner_new_card_draft",
      schemaVersion: NEW_CARD_DRAFT_SCHEMA_VERSION,
      ownerUserID: owner,
      expiresAt: expiresAt.toISOString(),
      draft: normalizeNewCardDraft(draft),
    };
    this.storage.setItem(
      NEW_CARD_DRAFT_STORAGE_KEY,
      JSON.stringify(stored),
    );
  }

  clear(): void {
    this.storage.removeItem(NEW_CARD_DRAFT_STORAGE_KEY);
  }
}

function isStoredDraft(value: unknown): value is StoredNewCardDraft {
  if (!isRecord(value)) return false;
  if (
    value.kind !== "owner_new_card_draft"
    || value.schemaVersion !== NEW_CARD_DRAFT_SCHEMA_VERSION
    || typeof value.ownerUserID !== "string"
    || !value.ownerUserID.trim()
    || typeof value.expiresAt !== "string"
    || Number.isNaN(new Date(value.expiresAt).valueOf())
    || !isNewCardDraft(value.draft)
  ) {
    return false;
  }
  const expectedExpiry = (
    new Date(value.draft.createdAt).valueOf() + NEW_CARD_DRAFT_MAX_AGE_MS
  );
  return new Date(value.expiresAt).valueOf() === expectedExpiry;
}

function emptyLoadResult(): NewCardDraftLoadResult {
  return {
    draft: null,
    recoveredInvalidState: false,
    expired: false,
    ownerMismatch: false,
  };
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}
